import type { IRadioContext, RadioTabProps, RadioTabSize, RadioTabsProps } from './const';
import { cn } from '@utils';
import { IconArrowLeft, IconArrowRight } from '@wegic/icon';
import { useGetState } from 'ahooks';
import { throttle } from 'lodash-es';

import React, { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import Message from '../Message';
import { RadioTabDisplayName } from './const';
import { getMinHeaderOffset, getRectDiff, getWidthTabList, setTransformStyle } from './handle';
import RadioTabsShadow from './Shadow';
import useScroll from './useScroll';
import './index.less';

export const RadioContext = React.createContext<IRadioContext>({
  value: null,
  onTabClick: () => {},
  size: 'default',
});

export interface RadioTabsRef {
  handleWrapperResize: () => void;
  handleScrollToRight: () => void;
  updateScrollButtonsState: () => void;
  scrollToTabByIndex: (index: number) => void;
}

function RadioTabs({ ref, ...props }: RadioTabsProps & { ref?: React.RefObject<RadioTabsRef | null> }) {
  const {
    size = 'default',
    className = '',
    containerStyle,
    sliderStyle,
    id,
    scrollable,
    availableWidth,
    disabled,
    disabledTip,
    tabGap = 2,
    onMouseDown,
    sliderClassName,
    type = 'default',
  } = props;
  const containerRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const sliderRef = useRef<HTMLDivElement>(null);
  const containerResizeObserverRef = useRef<ResizeObserver | null>(null);
  const wrapperResizeObserverRef = useRef<ResizeObserver | null>(null);
  const [tabWidthArr, setTabWidthArr, getTabWidthArr] = useGetState<number[]>([]);
  const [currentValue, setCurrentValue] = useState<string>('');
  const [activeIndex, setActiveIndex] = useState<number>(0);
  const [tabChangeTransition, setTabChangeTransition] = useState(false);
  const [headerOffset, setHeaderOffset] = useState(0);
  const [preButtonDisabled, setPreButtonDisabled] = useState(true);
  const [nextButtonDisabled, setNextButtonDisabled] = useState(false);
  const containerWidth = containerRef.current?.clientWidth;

  const isScrollControlsVisible = useMemo(() => {
    if (!scrollable) {
      return false;
    }

    if (containerWidth && availableWidth) {
      return containerWidth > availableWidth;
    }

    return false;
  }, [scrollable, availableWidth, containerWidth]);

  const maxHeaderOffset = 0;
  const additionalScrollWidth = 10;
  const getMinHeaderOffsetFunc = useCallback(
    () => getMinHeaderOffset(containerRef, wrapperRef, additionalScrollWidth),
    [],
  );

  const updateScrollButtonsState = () => {
    const gap = 1.5; // 由于最后 1.5px 难以滑动，保证流畅性，进行忽略。
    const isPreButtonDisabled = headerOffset >= maxHeaderOffset - gap;
    const isNextButtonDisabled = headerOffset <= getMinHeaderOffsetFunc() + gap;
    setPreButtonDisabled(isPreButtonDisabled);
    setNextButtonDisabled(isNextButtonDisabled);
  };

  useEffect(() => {
    updateScrollButtonsState();
  }, [headerOffset]);

  const calcTabsWidth = () => {
    const children = containerRef.current?.children ?? [];
    if (children.length === 0) {
      return;
    }
    const widthArr = Array.from(children).map(item => item.clientWidth);
    if (widthArr.every(item => item === 0) && getTabWidthArr().length > 0) {
      return;
    }
    setTabWidthArr(widthArr.slice(0, widthArr.length - 1));
  };

  const getTabIndex = () => {
    const { children } = props;
    const arr = React.Children.toArray(children);
    return arr.findIndex(child => {
      const childComponent = child as React.FunctionComponentElement<RadioTabProps>;
      return childComponent.props.value === currentValue;
    });
  };

  const getSliderWidth = useMemo(() => tabWidthArr?.[activeIndex] || 0, [tabWidthArr, activeIndex]);

  function handleHeaderOffset(offset: number, type: 'min' | 'max') {
    if (type === 'min') {
      const threshold = getMinHeaderOffsetFunc();
      const overThreshold = offset < threshold;
      return overThreshold ? threshold : offset;
    }

    const threshold = maxHeaderOffset;
    const overThreshold = offset > threshold;
    return overThreshold ? threshold : offset;
  }

  const getSliderLeft = useMemo(() => {
    let sum = 0;
    for (let i = 0; i < activeIndex; i++) {
      sum += tabWidthArr[i] + tabGap;
    }
    return sum;
  }, [tabWidthArr, activeIndex]);

  /** 改变视窗的偏移量，保持目标元素是可见的 */
  const ensureTabVisible = (target: HTMLElement) => {
    if (!wrapperRef.current) {
      return;
    }

    const nextTargetWidth = target.nextElementSibling?.clientWidth || 0;
    const preTargetWidth = target.previousElementSibling?.clientWidth || 0;
    const rectDiff = getRectDiff(target, wrapperRef.current);
    const diff = rectDiff.left - wrapperRef.current.clientWidth;
    const CovedSize = diff + target.clientWidth;
    if (CovedSize > 0) {
      // 最后的元素被遮挡了
      const translateX = headerOffset - CovedSize - additionalScrollWidth - nextTargetWidth / 2;
      setHeaderOffset(handleHeaderOffset(translateX, 'min'));
    } else if (rectDiff.left < 0) {
      // 第一个元素被遮挡了
      const translateX = headerOffset - rectDiff.left + additionalScrollWidth + preTargetWidth / 2;
      setHeaderOffset(handleHeaderOffset(translateX, 'max'));
    }
  };

  const handleTabClick = (value: string, e: React.MouseEvent<HTMLDivElement>) => {
    if (disabled) {
      Message.warning(disabledTip || '操作正在进行中');
      return;
    }
    if (value === currentValue) {
      return;
    }
    setTabChangeTransition(true);
    setCurrentValue(value);
    props.onChange && props.onChange(value);

    if (scrollable) {
      const target = e.currentTarget as HTMLElement;
      ensureTabVisible(target);
    }
  };

  const scrollToTabByIndex = (index: number) => {
    ensureTabVisible(containerRef.current?.children[index] as HTMLElement);
  };

  const handleScrollToRight = () => {
    setHeaderOffset(getMinHeaderOffsetFunc());
  };

  const handleHeaderOffsetResize = () => {
    const minHeaderOffset = getMinHeaderOffsetFunc();

    if (headerOffset < minHeaderOffset) {
      setHeaderOffset(minHeaderOffset);
    }
  };

  const handleContainerResize = useCallback(() => {
    calcTabsWidth();
  }, []);

  const handleWrapperResize = useCallback(() => {
    handleHeaderOffsetResize();
  }, [headerOffset]);

  useEffect(() => {
    const index = getTabIndex();
    setActiveIndex(index > -1 ? index : 0);
  }, [currentValue]);

  useEffect(() => {
    setCurrentValue(props.value || props.defaultValue);
    calcTabsWidth();
    const handleContainerResizeFn = throttle(handleContainerResize, 500, { trailing: true });
    containerResizeObserverRef.current = new ResizeObserver(handleContainerResizeFn);
    containerRef.current && containerResizeObserverRef.current.observe(containerRef.current);

    return () => {
      containerResizeObserverRef.current?.disconnect();
    };
  }, []);

  useEffect(() => {
    const handleWrapperResizeFn = throttle(handleWrapperResize, 200, { trailing: true });
    wrapperResizeObserverRef.current = new ResizeObserver(handleWrapperResizeFn);
    wrapperRef.current && wrapperResizeObserverRef.current.observe(wrapperRef.current);

    return () => {
      wrapperResizeObserverRef.current?.disconnect();
    };
  }, [headerOffset]);

  useEffect(() => {
    if (props.value !== undefined) {
      setCurrentValue(props.value);
    }
  }, [props.value]);

  const renderChildren = () => {
    const { children, size = 'default' } = props;
    return React.Children.map(children, (child, index) => {
      const childComponent = child as React.FunctionComponentElement<RadioTabProps & { size: RadioTabSize }>;
      if (childComponent.type.displayName === RadioTabDisplayName) {
        return React.cloneElement(childComponent, { size, scrollable, isActive: index === activeIndex });
      }
      console.error('<RadioTabs /> has a child which is not <RadioTab />');
      return <></>;
    });
  };

  const context = {
    value: currentValue,
    onTabClick: handleTabClick,
    size,
  };

  const onScroll = (translateX: number) => {
    if (translateX > 0 || translateX < getMinHeaderOffsetFunc()) {
      return;
    }
    setHeaderOffset(translateX);
  };

  useScroll({
    containerRef,
    headerOffset,
    onScroll,
    isScrollControlsVisible,
  });

  const tabsContainerStyle = setTransformStyle(`translateX(${headerOffset}px)`);

  const prevButtonClick = () => {
    if (!containerRef.current || !wrapperRef.current || disabled) {
      return;
    }
    const tabWidthList = getWidthTabList(containerRef.current.children);
    // 根据 containerRef.current 的宽度，和 headerOffset 计算出第几个元素被遮挡了
    const tabOffset = -headerOffset;
    let preTabWidth = 0;
    for (let i = 0; i < tabWidthList.length; i++) {
      preTabWidth += tabWidthList[i];
      if (preTabWidth > tabOffset) {
        const offset =
          tabWidthList[i] - preTabWidth + (wrapperRef.current.clientWidth - tabWidthList[i]) - additionalScrollWidth; // additionalScrollWidth 去掉遮罩阴影
        setHeaderOffset(handleHeaderOffset(offset, 'max'));
        break;
      }
    }
  };

  const nextButtonClick = () => {
    if (!containerRef.current || !wrapperRef.current || disabled) {
      return;
    }
    const tabWidthList = getWidthTabList(containerRef.current.children);
    // 根据 wrapper 的宽度，和 headerOffset 计算出第几个元素被遮挡了
    const tabOffset = -headerOffset + wrapperRef.current.clientWidth;
    let preTabWidth = 0;
    for (let i = 0; i < tabWidthList.length; i++) {
      preTabWidth += tabWidthList[i];
      if (preTabWidth > tabOffset) {
        const offset = tabWidthList[i] - preTabWidth - 2; // 2px 去掉滑块阴影
        setHeaderOffset(handleHeaderOffset(offset, 'min'));
        break;
      }
    }
  };

  const scrollControls = (
    <div className="wg-radio-tabs__scroll-controls">
      {!nextButtonDisabled && isScrollControlsVisible && <RadioTabsShadow direction="right" />}
      <div className="wg-radio-tabs__scroll-controls-buttons">
        <div
          className={cn('wg-radio-tabs__icon-button', { 'wg-radio-tabs__icon-button--disabled': preButtonDisabled })}
          onClick={prevButtonClick}
        >
          <IconArrowLeft />
        </div>
        <div
          className={cn('wg-radio-tabs__icon-button', { 'wg-radio-tabs__icon-button--disabled': nextButtonDisabled })}
          onClick={nextButtonClick}
        >
          <IconArrowRight />
        </div>
      </div>
    </div>
  );

  useImperativeHandle(ref, () => ({
    handleWrapperResize,
    handleScrollToRight,
    updateScrollButtonsState,
    scrollToTabByIndex,
  }));

  return (
    <div
      className={cn('wg-radio-tabs-box', { 'wg-radio-tabs-box--scrollable': scrollable })}
      style={props.style}
      onMouseDown={onMouseDown}
    >
      {!preButtonDisabled && isScrollControlsVisible && <RadioTabsShadow direction="left" />}
      <div
        className={cn('wg-radio-tabs-wrapper', {
          'wg-radio-tabs-wrapper--scrollable': scrollable,
          'wg-radio-tabs-wrapper--plain': type === 'plain',
        })}
        ref={wrapperRef}
      >
        <div
          className={cn(
            'wg-radio-tabs',
            `wg-radio-tabs--size-${size}`,
            {
              'wg-radio-tabs--scrollable': scrollable,
            },
            className,
          )}
          ref={containerRef}
          id={id}
          style={{
            ...(isScrollControlsVisible ? tabsContainerStyle : {}),
            ...(tabGap ? { gap: `${tabGap}px` } : {}),
          }}
        >
          <RadioContext value={context}>{renderChildren()}</RadioContext>
          <div className="wg-radio-tabs__slider-container" style={containerStyle}>
            <div
              className={cn('wg-radio-tabs__slider', sliderClassName, {
                'wg-radio-tabs__slider--transition': tabChangeTransition,
              })}
              ref={sliderRef}
              style={{
                width: `${getSliderWidth}px`,
                left: `${getSliderLeft}px`,
                opacity: `${activeIndex === -1 ? 0 : 1}`,
                ...sliderStyle,
              }}
            >
            </div>
          </div>
        </div>
      </div>
      {!nextButtonDisabled && scrollable && (
        <RadioTabsShadow direction="right" className="wg-radio-tabs__mobile-right-shadow" />
      )}
      {isScrollControlsVisible && scrollControls}
    </div>
  );
}

RadioTabs.displayName = 'RadioTabs';

export default RadioTabs;
